import { useState } from 'react';
import { Flask, Pencil, Plus, Trash } from '@phosphor-icons/react';
import type { Lab, LabKind } from '@cvs/shared';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, Card, EmptyState, ErrorState, LoadingState, Modal, Pill } from '../components/ui';
import { HtmlUploadField } from '../components/HtmlUploadField';

type LabModal = { lab?: Lab } | null;

export function StudioLabs() {
  // Labs plus the curriculum, so a lab can be (re)assigned to a class.
  const state = useAsync(
    () =>
      Promise.all([api.labs(), api.admin.content()]).then(([l, c]) => ({
        labs: l.labs,
        modules: c.modules,
        sections: c.sections,
      })),
    [],
  );
  const [modal, setModal] = useState<LabModal>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.loading) return <LoadingState />;
  if (state.error || !state.data) return <ErrorState message={state.error ?? 'Failed to load'} onRetry={state.reload} />;

  const { labs, modules, sections } = state.data;
  const classLabel = (moduleId: string | null) => {
    if (!moduleId) return null;
    const m = modules.find((x) => x.id === moduleId);
    return m ? m.title : null;
  };

  async function run(fn: () => Promise<unknown>, after?: () => void) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      state.reload();
      after?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <div className="label-mono">Labs &amp; Exercises</div>
          <h2 className="text-xl font-bold">Lab &amp; exercise library</h2>
          <p className="mt-1 text-sm text-slate-500">
            Standalone hands-on labs and practice exercises (e.g. phishing samples). Section projects are managed in
            Content.
          </p>
        </div>
        <Button icon={Plus} onClick={() => setModal({})}>
          Add
        </Button>
      </div>

      {error && <p className="mb-4 text-sm font-medium text-rose-600">{error}</p>}

      {labs.length === 0 ? (
        <EmptyState icon={Flask} title="Nothing here yet" description="Add your first lab or exercise." />
      ) : (
        <div className="space-y-3">
          {labs.map((l) => (
            <Card key={l.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-brand-navy">{l.title}</span>
                    {l.kind === 'exercise' && <Pill tone="violet">Exercise</Pill>}
                    {l.kind === 'project' && <Pill tone="amber">Project</Pill>}
                    <Pill tone="teal">{l.points} pts</Pill>
                    {l.env && <Pill tone="slate">{l.env}</Pill>}
                    {l.kind !== 'project' &&
                      (classLabel(l.moduleId) ? (
                        <Pill tone="slate">📘 {classLabel(l.moduleId)}</Pill>
                      ) : (
                        <Pill tone="amber">No class</Pill>
                      ))}
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-500">{l.instructions}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="secondary" icon={Pencil} onClick={() => setModal({ lab: l })}>
                    Edit
                  </Button>
                  <button
                    className="btn-ghost p-2 text-rose-600"
                    aria-label="Delete lab"
                    onClick={() => {
                      if (confirm(`Delete "${l.title}"?`)) void run(() => api.admin.deleteLab(l.id));
                    }}
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {modal && (
        <Modal
          open
          onClose={() => setModal(null)}
          title={modal.lab ? `Edit ${modal.lab.kind === 'exercise' ? 'exercise' : 'lab'}` : 'Add lab or exercise'}
        >
          <LabForm
            key={modal.lab?.id ?? 'new'}
            initial={modal.lab}
            modules={modules}
            sections={sections}
            busy={busy}
            onCancel={() => setModal(null)}
            onSubmit={(v) =>
              run(() => (modal.lab ? api.admin.updateLab(modal.lab.id, v) : api.admin.createLab(v)), () => setModal(null))
            }
          />
        </Modal>
      )}
    </div>
  );
}

function LabForm({
  initial,
  modules,
  sections,
  busy,
  onSubmit,
  onCancel,
}: {
  initial?: Lab;
  modules: { id: string; title: string; sectionId: string | null; order: number }[];
  sections: { id: string; title: string }[];
  busy: boolean;
  onSubmit: (v: {
    title: string;
    kind: LabKind;
    points: number;
    env: string;
    instructions: string;
    resourceUrl: string | null;
    guideHtml: string | null;
    answerHtml: string | null;
    moduleId: string | null;
  }) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  // This library manages standalone labs + exercises; projects are section-scoped.
  const [kind, setKind] = useState<LabKind>(initial?.kind === 'exercise' ? 'exercise' : 'lab');
  const [points, setPoints] = useState(String(initial?.points ?? 100));
  const [env, setEnv] = useState(initial?.env ?? '');
  const [resourceUrl, setResourceUrl] = useState(initial?.resourceUrl ?? '');
  const [guideHtml, setGuideHtml] = useState(initial?.guideHtml ?? '');
  const [answerHtml, setAnswerHtml] = useState(initial?.answerHtml ?? '');
  const [instructions, setInstructions] = useState(initial?.instructions ?? '');
  const [moduleId, setModuleId] = useState<string>(initial?.moduleId ?? '');
  const isExercise = kind === 'exercise';
  // Classes grouped under their section, in curriculum order.
  const grouped = sections.map((s) => ({
    section: s,
    classes: modules.filter((m) => m.sectionId === s.id).sort((a, b) => a.order - b.order),
  }));
  const unsectioned = modules.filter((m) => !m.sectionId);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          title: title.trim(),
          kind,
          points: Number(points) || 0,
          env: env.trim(),
          instructions: instructions.trim(),
          resourceUrl: resourceUrl.trim() || null,
          guideHtml: guideHtml.trim() || null,
          answerHtml: answerHtml.trim() || null,
          moduleId: moduleId || null,
        });
      }}
    >
      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="label-mono mb-1 block">Title</span>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>
        <label className="block">
          <span className="label-mono mb-1 block">Type</span>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value as LabKind)}>
            <option value="lab">Hands-on lab</option>
            <option value="exercise">Exercise</option>
          </select>
        </label>
      </div>
      <label className="block">
        <span className="label-mono mb-1 block">Class</span>
        <select className="input" value={moduleId} onChange={(e) => setModuleId(e.target.value)}>
          <option value="">— Not linked to a class —</option>
          {grouped.map(({ section, classes }) =>
            classes.length === 0 ? null : (
              <optgroup key={section.id} label={section.title}>
                {classes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
              </optgroup>
            ),
          )}
          {unsectioned.length > 0 && (
            <optgroup label="No section">
              {unsectioned.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.title}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <p className="mt-1 text-xs text-slate-400">
          The class this {isExercise ? 'exercise' : 'lab'} appears under — change it to move it to a different class.
        </p>
      </label>
      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="label-mono mb-1 block">Points</span>
          <input className="input" type="number" min={0} value={points} onChange={(e) => setPoints(e.target.value)} />
        </label>
        <label className="block">
          <span className="label-mono mb-1 block">Environment</span>
          <input className="input" value={env} onChange={(e) => setEnv(e.target.value)} placeholder="Azure Sandbox" />
        </label>
      </div>
      <label className="block">
        <span className="label-mono mb-1 block">Guide link (PDF / SharePoint) — optional</span>
        <input
          className="input"
          type="url"
          value={resourceUrl}
          onChange={(e) => setResourceUrl(e.target.value)}
          placeholder="https://…sharepoint.com/…/lab-guide.pdf"
        />
        <p className="mt-1 text-xs text-slate-400">
          A PDF renders inline on the lab page (page-by-page, like the class slides). SharePoint links are resolved for
          students automatically.
        </p>
      </label>
      <HtmlUploadField
        label={isExercise ? 'Question page (HTML) — optional' : 'Inline HTML guide — optional'}
        value={guideHtml}
        onChange={setGuideHtml}
        placeholder="<h1>…</h1>  — paste or upload a self-contained HTML page"
        hint={
          isExercise
            ? 'The exercise question — renders inline (auto-height, sandboxed) for students.'
            : 'Renders inline on the page (sandboxed) — an alternative to a PDF link.'
        }
      />
      <HtmlUploadField
        label="Answer page (HTML) — optional"
        value={answerHtml}
        onChange={setAnswerHtml}
        placeholder="<h1>Answer…</h1>  — paste or upload the model answer"
        hint="Students get a “Reveal answer” button below the question — ideal for exercises."
      />
      <label className="block">
        <span className="label-mono mb-1 block">Instructions</span>
        <textarea className="input" rows={5} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
      </label>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={busy} disabled={!title.trim()}>
          Save
        </Button>
      </div>
    </form>
  );
}
